/**
 * ActorsPage — modern profile-card cast library.
 *
 * Each actor is a contemporary card with a 3:4 portrait crop, name, age,
 * and visual-tag chips. Modal redesigned as a clean modern form sheet.
 */
import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type ActorRecord } from '@ponpon/api-client'
import { useAuth } from '../contexts/AuthContext'

export function ActorsPage() {
  const { session, loading } = useAuth()
  const [createOpen, setCreateOpen] = useState(false)
  const [armedDelete, setArmedDelete] = useState<string | null>(null)
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['actors'],
    queryFn: async () => (await api.listActors()).actors,
    refetchInterval: 10_000,
    staleTime: 5_000,
  })

  const del = useMutation({
    mutationFn: async (id: string) => api.deleteActor(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['actors'] }),
  })

  if (loading) return <div className="p-12 text-text-3 text-[0.92rem]">Loading…</div>
  if (!session) return <Navigate to="/login" replace />

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24">
      {/* Header */}
      <div className="flex items-end justify-between gap-6 mb-12">
        <div>
          <div className="pill mb-3">
            <span className="live-dot" />
            <span>Cast · {data?.length ?? 0}</span>
          </div>
          <h1 className="h-display text-[clamp(2.4rem,5.4vw,4.6rem)] text-text">
            The <span className="text-accent">cast</span>.
          </h1>
          <p className="mt-4 text-text-2 text-[1.05rem] max-w-xl">
            Reusable identities. Drop a face into any scene, recast in one click.
          </p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="btn-accent shrink-0">
          + New actor
        </button>
      </div>

      {isLoading && <div className="text-text-3 py-6">Loading…</div>}
      {error && (
        <div className="text-err py-6 text-[0.92rem]">
          ⨯ Failed to load: {String((error as Error).message ?? error)}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="card p-16 text-center">
          <div className="pill mx-auto mb-4">
            <span>Empty cast</span>
          </div>
          <h2 className="h-display text-[2.2rem] text-text-2 leading-tight">
            No faces yet.
          </h2>
          <p className="h-italic text-text-3 mt-2 mb-8 text-[1.15rem]">
            Cast your first one — drop a reference photo.
          </p>
          <button onClick={() => setCreateOpen(true)} className="btn-accent">+ New actor</button>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {data.map((a) => (
            <ActorCard
              key={a.id}
              actor={a}
              onDelete={() => {
                // Inline two-step confirm (no native confirm() chrome): first
                // press arms, second press within 3s removes. Reversible-ish
                // action, so no modal — but cast removal isn't undoable via
                // API yet, hence the armed step rather than optimistic+undo.
                if (a.is_celebrity) return
                if (armedDelete === a.id) {
                  setArmedDelete(null)
                  del.mutate(a.id)
                } else {
                  setArmedDelete(a.id)
                  window.setTimeout(
                    () => setArmedDelete((cur) => (cur === a.id ? null : cur)),
                    3000,
                  )
                }
              }}
              deleteArmed={armedDelete === a.id}
            />
          ))}
        </div>
      )}

      {createOpen && (
        <CreateActorModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false)
            qc.invalidateQueries({ queryKey: ['actors'] })
          }}
        />
      )}
    </section>
  )
}

function ActorCard({ actor, onDelete, deleteArmed = false }: { actor: ActorRecord; onDelete: () => void; deleteArmed?: boolean }) {
  return (
    <article className="card card-hover group">
      {/* Portrait */}
      <div className="relative aspect-[3/4] bg-bg-2 overflow-hidden">
        {actor.portrait ? (
          <img
            src={`/actors/${actor.portrait}`}
            alt={`${actor.name}`}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="text-[3.6rem] text-text-4 leading-none"
              style={{ fontFamily: 'var(--font-italic)', fontStyle: 'italic' }}
            >
              {actor.name.slice(0, 1)}
            </div>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
              <div className="pill pill--coral">
                <span className="live-dot live-dot--coral" />
                <span>generating</span>
              </div>
            </div>
          </div>
        )}
        {actor.is_celebrity && (
          <div className="absolute top-3 left-3">
            <div className="pill"><span>★ celebrity</span></div>
          </div>
        )}
        {/* Bottom fade for legibility */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-surface to-transparent pointer-events-none" />
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="flex items-baseline justify-between gap-2">
          <h2
            className="text-[1.12rem] text-text leading-tight truncate"
            style={{ fontVariationSettings: '"wdth" 96, "opsz" 96', fontWeight: 600, letterSpacing: '-0.018em' }}
          >
            {actor.name}
          </h2>
          {actor.age != null && (
            <span className="t-mono text-[0.74rem] text-text-3 shrink-0">{actor.age}y</span>
          )}
        </div>

        {actor.visual_tags?.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1">
            {actor.visual_tags.slice(0, 3).map((tag) => (
              <span key={tag} className="inline-block px-2 py-0.5 rounded bg-white/[0.04] text-[0.7rem] text-text-2 truncate">
                {tag}
              </span>
            ))}
            {actor.visual_tags.length > 3 && (
              <span className="inline-block px-2 py-0.5 rounded bg-white/[0.04] text-[0.7rem] text-text-3">
                +{actor.visual_tags.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-line flex items-center justify-between text-[0.78rem]">
          <span className="text-text-3">In cast</span>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete() }}
            disabled={actor.is_celebrity}
            title={actor.is_celebrity ? 'Celebrity actors are read-only' : undefined}
            className={`transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
              deleteArmed
                ? 'text-err font-semibold'
                : 'text-text-3 hover:text-err'
            }`}
          >
            {deleteArmed ? 'Confirm remove?' : 'Remove'}
          </button>
        </div>
      </div>
    </article>
  )
}

function CreateActorModal({
  onClose, onCreated,
}: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [age, setAge] = useState('')
  const [tagsRaw, setTagsRaw] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Pick a reference photo.')
      const tags = tagsRaw.split(',').map((t) => t.trim()).filter(Boolean)
      const ageInt = age.trim() ? parseInt(age, 10) : undefined
      if (age.trim() && (ageInt === undefined || isNaN(ageInt))) {
        throw new Error('Age must be a number.')
      }
      return api.createActor({ name: name.trim(), referenceImage: file, age: ageInt, visualTags: tags })
    },
    onSuccess: onCreated,
    onError: (err: Error) => setError(err.message),
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          setError(null)
          if (!name.trim()) { setError('Name required.'); return }
          create.mutate()
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md glass rounded-lg shadow-lg"
      >
        <div className="px-6 py-5 border-b border-line">
          <h2
            className="text-[1.6rem] text-text leading-tight"
            style={{ fontVariationSettings: '"wdth" 96, "opsz" 96', fontWeight: 600, letterSpacing: '-0.022em' }}
          >
            New actor
          </h2>
          <p className="text-text-3 text-[0.86rem] mt-1">A face you can cast into any scene.</p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} required className="field" />
          </Field>
          <Field label="Age (optional)">
            <input value={age} onChange={(e) => setAge(e.target.value)} type="number" min="0" max="200" className="field" />
          </Field>
          <Field label="Visual tags · comma-separated">
            <input
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="black hair, tall, asian"
              className="field"
            />
          </Field>
          <Field label="Reference photo">
            <div className="relative">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-text-3 text-[0.88rem]
                  file:bg-white/[0.05] file:border file:border-line-2 file:text-text
                  file:rounded-md file:px-3 file:py-2 file:mr-3
                  file:font-medium file:text-[0.85rem] file:cursor-pointer
                  hover:file:bg-white/[0.08]"
              />
            </div>
            {file && <p className="mt-1.5 t-mono text-[0.74rem] text-text-3 truncate">↳ {file.name}</p>}
          </Field>

          {error && (
            <p className="text-err text-[0.86rem]">⨯ {error}</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-line flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">
            Cancel
          </button>
          <button type="submit" disabled={create.isPending} className="btn-accent">
            {create.isPending ? 'Casting…' : 'Cast actor →'}
          </button>
        </div>
      </form>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-text-3 text-[0.78rem] mb-1.5">{label}</span>
      {children}
    </label>
  )
}
